import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const REDIRECT_WHITELIST = ["/invitations"];
const REDIRECT_KEY = "postLoginRedirect";

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { authState, initializing } = useAuth();
  const location = useLocation();

  // Gate on the initial session check only. authState.loading also flips on
  // every login/signup action and would unmount the page mid-flow.
  if (initializing) {
    return null;
  }

  if (!authState.isAuthenticated) {
    const target = location.pathname + location.search;
    if (REDIRECT_WHITELIST.some((p) => location.pathname.startsWith(p))) {
      localStorage.setItem(REDIRECT_KEY, target);
    }
    return <Navigate to="/login" />;
  }

  // Auth user with no users/{uid} doc: registration was started but never
  // finished — resume the onboarding wizard (it picks up at the invite step).
  if (
    authState.isAuthenticated &&
    !authState.user &&
    authState.error === "User data not found"
  ) {
    return <Navigate to="/onboarding" />;
  }

  if (
    authState.user?.deActivated &&
    location.pathname !== "/account-deactivated"
  ) {
    return <Navigate to="/account-deactivated" />;
  }

  if (
    authState.user?.deletionStatus === "ready" &&
    location.pathname !== "/complete-account-removal"
  ) {
    return <Navigate to="/complete-account-removal" />;
  }

  if (
    authState.user &&
    !authState.user.onboardingComplete &&
    location.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
